import React from 'react';
import { i18n } from 'config/locales';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';

import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import Textarea from 'components/Form/Textarea';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import { isPVQForm } from 'helpers/form';
import Text from 'components/Form/Text';
import PVQLocation from 'components/Form/PVQLocation';
import Branch from 'components/Form/Branch';
import PVQName from 'components/Form/PVQName';
import { ForeignActivitiesOnGoingSupportItemModel } from 'forms/sections/foreign/activities/ongoingsupport/types';
import { foreignBenefitFrequencyOptions, foreignBenefitTypes, foreignInterestOwnerOptions } from 'constants/enums/foreignActivityOptions';
import Dropdown from 'components/Form/Dropdown';
import DateControl from 'components/Form/DateControl';

interface Props extends Partial<ForeignActivitiesOnGoingSupportItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  formType?: string

  dispatch: Dispatch
}

export default class OnGoingSupportItem extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.includes('required')
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignActivitiesOnGoingSupportItemModel>) => {
    this.props.onUpdate({

      EligiblePersonOptions: this.props.EligiblePersonOptions,
      EligiblePersonName: this.props.EligiblePersonName,
      FutureBenefitType: this.props.FutureBenefitType,
      FutureBenefitOptionsOtherExplanation: this.props.FutureBenefitOptionsOtherExplanation,
      ExpectedDate: this.props.ExpectedDate,
      FrequencyOptions: this.props.FrequencyOptions,
      FrequencyOptionsOtherExplanation: this.props.FrequencyOptionsOtherExplanation,
      EligiblePersonCountry: this.props.EligiblePersonCountry,
      EligibleExplanation: this.props.EligibleExplanation,
      TotalBenefitValue: this.props.TotalBenefitValue,
      Accepting: this.props.Accepting,
      AcceptingExplanation: this.props.AcceptingExplanation,
      Obligated: this.props.Obligated,
      ObligatedExplanation: this.props.ObligatedExplanation,
      ...queue,
    });
  };

  updateEligiblePersonOptions = (values: ForeignActivitiesOnGoingSupportItemModel['EligiblePersonOptions']) => {
    this.update({
      EligiblePersonOptions: values,
    });
  };

  updateEligiblePersonName = (values: ForeignActivitiesOnGoingSupportItemModel['EligiblePersonName']) => {
    this.update({
      EligiblePersonName: values,
    });
  };

  updateFutureBenefitType = (values: ForeignActivitiesOnGoingSupportItemModel['FutureBenefitType']) => {
    this.update({
      FutureBenefitType: values,
    });
  };

  updateFutureBenefitOptionsOtherExplanation = (values: ForeignActivitiesOnGoingSupportItemModel['FutureBenefitOptionsOtherExplanation']) => {
    this.update({
      FutureBenefitOptionsOtherExplanation: values,
    });
  };

  updateExpectedDate = (values: ForeignActivitiesOnGoingSupportItemModel['ExpectedDate']) => {
    this.update({
      ExpectedDate: values,
    });
  };

  updateFrequencyOptions = (values: ForeignActivitiesOnGoingSupportItemModel['FrequencyOptions']) => {
    this.update({
      FrequencyOptions: values,
    });
  };

  updateFrequencyOptionsOtherExplanation = (values: ForeignActivitiesOnGoingSupportItemModel['FrequencyOptionsOtherExplanation']) => {
    this.update({
      FrequencyOptionsOtherExplanation: values,
    });
  };

  updateEligiblePersonCountry = (values: ForeignActivitiesOnGoingSupportItemModel['EligiblePersonCountry']) => {
    this.update({
      EligiblePersonCountry: values,
    });
  };

  updateEligibleExplanation = (values: ForeignActivitiesOnGoingSupportItemModel['EligibleExplanation']) => {
    this.update({
      EligibleExplanation: values,
    });
  };

  updateTotalBenefitValue = (values: ForeignActivitiesOnGoingSupportItemModel['TotalBenefitValue']) => {
    this.update({
      TotalBenefitValue: values,
    });
  };

  updateAccepting = (values: ForeignActivitiesOnGoingSupportItemModel['Accepting']) => {
    this.update({
      Accepting: values,
    });
  };

  updateAcceptingExplanation = (values: ForeignActivitiesOnGoingSupportItemModel['AcceptingExplanation']) => {
    this.update({
      AcceptingExplanation: values,
    });
  };

  updateObligated = (values: ForeignActivitiesOnGoingSupportItemModel['Obligated']) => {
    this.update({
      Obligated: values,
    });
  };

  updateObligatedExplanation = (values: ForeignActivitiesOnGoingSupportItemModel['ObligatedExplanation']) => {
    this.update({
      ObligatedExplanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const includesOther = (value: string) => typeof value === 'string' && value.includes('Other');
    const prefixLocale = `foreign.pvq.activities.benefit.titles`;
    return (
      <div className="ongoingsupport-item">

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EligiblePersonOptions`}
          title={i18n.t(`${prefixLocale}.who`)}
        >
          <Dropdown
            attribute={`${attribute}.EligiblePersonOptions`}
            name="interst-type"
            onError={this.props.onError}
            onUpdate={this.updateEligiblePersonOptions}
            value={this.props.EligiblePersonOptions?.value}
            required={this.props.required}
          >
            {foreignInterestOwnerOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
          {/*  when child show name */}
          {this.props.EligiblePersonOptions?.value === 'Dependent children' && (
            <PVQName
              name="EligiblePersonName"
              {...this.props.EligiblePersonName}
              onUpdate={this.updateEligiblePersonName}
              title={i18n.t('foreign.pvq.activities.financialInterest.dependentChild')}
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.EligiblePersonName`}
              showLastFirstMiddleNameOrder
            />
          )}

        </Field>

        <Field
          title={i18n.t('foreign.pvq.activities.benefit.titles.benefitDetails')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FutureBenefitType`}
        >
          <Dropdown
            attribute={`${attribute}.FutureBenefitType`}
            name="FutureBenefitType"
            onError={this.props.onError}
            onUpdate={this.updateFutureBenefitType}
            value={this.props.FutureBenefitType?.value}
            required={this.props.required}
          >
            {foreignBenefitTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
          {includesOther(this.props.FutureBenefitType?.value) && (
            <Textarea
              name="FutureBenefitOptionsOtherExplanation"
              label={`${prefixLocale}.explain`}
              className="FutureBenefitOptionsOtherExplanation"
              {...this.props.FutureBenefitOptionsOtherExplanation}
              onUpdate={this.updateFutureBenefitOptionsOtherExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute={`${attribute}.FutureBenefitOptionsOtherExplanation`}
            />
          )}

        </Field>

        <Field
          title={i18n.t(`${prefixLocale}.date`)}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ExpectedDate`}
        >
          <DateControl
            name="ExpectedDate"
            className="ExpectedDate"
            minDateEqualTo
            {...this.props.ExpectedDate}
            onUpdate={this.updateExpectedDate}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ExpectedDate`}
            hideDay
          />
        </Field>

        <Field
          title={i18n.t(`${prefixLocale}.frequency`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FrequencyOptions`}
        >
          <Dropdown
            attribute={`${attribute}.FrequencyOptions`}
            name="FrequencyOptions"
            onError={this.props.onError}
            onUpdate={this.updateFrequencyOptions}
            value={this.props.FrequencyOptions?.value}
            required={this.props.required}
          >
            {foreignBenefitFrequencyOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        {includesOther(this.props.FrequencyOptions?.value) && (
          <Field
            title={i18n.t(`${prefixLocale}.value`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.FrequencyOptionsOtherExplanation`}
          >
            <Textarea
              name="FrequencyOptionsOtherExplanation"
              label="Please explain."
              className="FrequencyOptionsOtherExplanation"
              {...this.props.FrequencyOptionsOtherExplanation}
              onUpdate={this.updateFrequencyOptionsOtherExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute={`${attribute}FrequencyOptionsOtherExplanation`}
            />
          </Field>
        )}

        <Field
          title={i18n.t(`${prefixLocale}.country`)}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EligiblePersonCountry`}
        >
          <Country
            name="EligiblePersonCountry"
            {...this.props.EligiblePersonCountry}
            onUpdate={this.updateEligiblePersonCountry}
            onError={this.props.onError}
            className="EligiblePersonCountry"
            required={this.props.required}
            attribute={`${attribute}.EligiblePersonCountry`}
          />
        </Field>

        <Field
          attribute={`${attribute}.EligibleExplanation`}
          title={i18n.t(`${prefixLocale}.why`)}
        >
          <Textarea
            name="EligibleExplanation"
            {...this.props.EligibleExplanation}
            onUpdate={this.updateEligibleExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.EligibleExplanation`}
          />
        </Field>

        <Field
          title={i18n.t(`${prefixLocale}.value`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TotalBenefitValue`}
        >
          <Currency
            name="TotalBenefitValue"
            className="TotalBenefitValue"
            {...this.props.TotalBenefitValue}
            onUpdate={this.updateTotalBenefitValue}
            min="0"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TotalBenefitValue`}
          />
        </Field>
        <Branch
          name="Accepting"
          label={i18n.t(`${prefixLocale}.accept`)}
          attribute={`${attribute}.Accepting`}
          exactAttribute
          labelSize="h4"
          className="Accepting no-margin-bottom"
          {...this.props.Accepting}
          onUpdate={this.updateAccepting}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Field
          title={i18n.t(`${prefixLocale}.explain`)}
          attribute={`${attribute}.AcceptingExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >

          <Textarea
            name="AcceptingExplanation"
            {...this.props.AcceptingExplanation}
            onUpdate={this.updateAcceptingExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.AcceptingExplanation`}
          />

        </Field>
        <Branch
          name="Obligated"
          label={i18n.t(`${prefixLocale}.obligate`)}
          attribute={`${attribute}.Obligated`}
          labelSize="h4"
          className="Obligated no-margin-bottom"
          {...this.props.Obligated}
          onUpdate={this.updateObligated}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Field
          title={i18n.t(`${prefixLocale}.explain`)}
          attribute={`${attribute}.ObligatedExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="ObligatedExplanation"
            {...this.props.ObligatedExplanation}
            onUpdate={this.updateObligatedExplanation}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.ObligatedExplanation`}
          />

        </Field>
      </div>
    );
  }
}

OnGoingSupportItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode) => arr,
  EligiblePersonOptions: {},
  EligiblePersonName: {},
  FutureBenefitType: {},
  FutureBenefitOptionsOtherExplanation: {},
  ExpectedDate: {},
  FrequencyOptions: {},
  FrequencyOptionsOtherExplanation: {},
  EligiblePersonCountry: {},
  EligibleExplanation: {},
  TotalBenefitValue: {},
  Accepting: {},
  AcceptingExplanation: {},
  Obligated: {},
  ObligatedExplanation: {},
};
